import React, { useMemo } from "react";
import {
  aggregationPeriodFromPresets,
  aggregationPeriodLabel,
  computePreviousAggregationInterval,
  aggregationIntervalToDateValue,
  type AggregationPeriod,
  aggregationIntervalWithDurationFromDateValue,
} from "../../../../domain/aggregation/period";
import WidgetCard from "../WidgetCard";
import ChartAverageLosses from "./ChartAverageLosses";
import useActiveAnalysesStream from "../../../../api/useActiveAnalysesStream";

import { useProject } from "../../../../api/useProjects";
import t from "../../../../i18n";
import { useTranslations } from "../../../../api/useTranslations";
import WidgetSelectorHeader, { widgetSelectorHeader } from "../WidgetSelectorHeader";
import { useSettings } from "../../../../api/useSettings";
import NoDataPeriodPlaceholderWidget from "../NoDataPeriodPlaceholderWidget";
import { css, cx } from "@linaria/core";
import { colors, fonts, spacings } from "@ui/Theme";
import { toBasenamePath, useBasename } from "../../../../router/helpers/navigation";
import { toPath } from "../../../../router/helpers/path";
import { ArrowRightUpLine } from "@ui/IconV2/react";
import { styled } from "@linaria/react";
import Link from "@ui/Link";
import useGroups from "../../../../api/useGroups";
import { useAggregationIntervalFromPeriodState } from "../useAggregationIntervalFromPeriodState";
import { eventAggregationPeriodPresets } from "../../../../domain/aggregation/preset";
import { useActivePowerPerformanceIndicator } from "../../../../api/usePerformanceIndicators";

const AverageLossesWidget = ({
  projectId,
  projectTimezone,
  className,
  sharedAggregationPeriod,
  hidePeriod = false,
  hideLink = true,
  disablePeriod = false,
}: {
  projectId: string;
  projectTimezone: string;
  className?: string;
  hidePeriod?: boolean;
  hideLink?: boolean;
  sharedAggregationPeriod: boolean;
  disablePeriod?: boolean;
}) => {
  const translations = useTranslations();
  const settings = useSettings();
  const settingsAggregationPeriod = settings.AGGREGATION_PERIOD as AggregationPeriod;
  const { activeAnalyses, isStreaming } = useActiveAnalysesStream(projectId);
  const activePowerPerformanceIndicator = useActivePowerPerformanceIndicator(projectId);
  const groups = useGroups(projectId);
  const project = useProject(projectId);
  if (!project) {
    throw new Error("invalid project");
  }

  const aggregationPeriodPresets = useMemo(
    () =>
      eventAggregationPeriodPresets({
        timezone: project.timezone,
        groups,
        limit: {
          from: new Date(project.analysesAggregate?.earliestActiveAnalysis?.date ?? Date.now()),
          to: new Date(),
        },
      }),
    [groups, project.analysesAggregate?.earliestActiveAnalysis?.date, project.timezone],
  );
  const [aggregationInterval, setAggregationInterval] = useAggregationIntervalFromPeriodState({
    projectId,
    projectTimezone,
    aggregationPeriod: settingsAggregationPeriod,
    shared: sharedAggregationPeriod,
    from: new Date(project.analysesAggregate?.earliestActiveAnalysis?.date ?? Date.now()),
  });
  const aggregationPeriod = aggregationPeriodFromPresets({
    dateValues: aggregationIntervalToDateValue(aggregationInterval, projectTimezone),
    presets: aggregationPeriodPresets,
  });
  const previousAggregationInterval = computePreviousAggregationInterval(aggregationInterval);

  const basename = useBasename();
  const path = activePowerPerformanceIndicator
    ? "/projects/:projectId/diagnosis"
    : "/projects/:projectId/fault-events";

  return (
    <WidgetCard className={cx(className, widgetCardWithHeaderMargin)}>
      <WidgetSelectorHeader
        disablePeriod={disablePeriod}
        title={t("project.fault_event.live.widget.average_losses.title", {
          translations,
        })}
        link={
          !hideLink && (
            <StyledLink
              to={toBasenamePath(toPath(path, { projectId }), basename ?? "/")}
              target="_blank"
            >
              <ArrowRightUpLine size="s" />
              {t("project.fault_event.live.widget.average_losses.link")}
            </StyledLink>
          )
        }
        tooltipText={t(
          "project.fault_event.live.widget.average_losses.tooltip",
          translations,
          aggregationPeriodLabel(
            aggregationPeriod ?? settingsAggregationPeriod,
          ).toLocaleLowerCase(),
        )}
        aggregationIntervalDateValue={aggregationIntervalToDateValue(
          aggregationInterval,
          projectTimezone,
        )}
        aggregationPeriodPresets={aggregationPeriodPresets}
        onAggregationIntervalChange={interval =>
          setAggregationInterval(
            aggregationIntervalWithDurationFromDateValue(
              interval,
              projectTimezone,
              aggregationPeriodPresets,
            ),
          )
        }
        hidePeriod={hidePeriod}
      />
      {activePowerPerformanceIndicator && aggregationPeriod ? (
        <ChartAverageLosses
          loading={isStreaming}
          project={project}
          performanceIndicator={activePowerPerformanceIndicator}
          activeAnalyses={activeAnalyses}
          aggregationInterval={aggregationInterval}
          previousAggregationInterval={previousAggregationInterval}
        />
      ) : (
        <div className={centeredPlaceholder}>
          <NoDataPeriodPlaceholderWidget />
        </div>
      )}
    </WidgetCard>
  );
};

export default AverageLossesWidget;

const StyledLink = styled(Link)`
  display: flex;
  align-items: baseline;
  color: ${colors.lightNitrogen500_Main};
  gap: ${spacings.xxs};
  font: ${fonts.body1};
  :hover {
    text-decoration: underline;
    color: ${colors.lightNitrogen600};
  }

  & > :nth-child(1) {
    align-self: center;
  }
`;

//#region CSS
const centeredPlaceholder = css`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const widgetCardWithHeaderMargin = css`
  .${widgetSelectorHeader} {
    margin-bottom: ${spacings.xs};
  }
`;
//#endregion CSS
